import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, Heart, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Pagina pubblica per l'assistito - Tasto Scudo Emergenza
const ScudoPage = () => {
  const { famigliaId } = useParams();
  const [clickCount, setClickCount] = useState(0);
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [assistitoName, setAssistitoName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [lastClickTime, setLastClickTime] = useState(0);
  
  // Reset click count dopo 3 secondi di inattività
  useEffect(() => {
    if (clickCount > 0 && clickCount < 3) {
      const timer = setTimeout(() => {
        setClickCount(0);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [clickCount, lastClickTime]);
  
  // Carica info assistito
  useEffect(() => {
    const fetchFamilyInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/scudo/info/${famigliaId}`);
        if (response.ok) {
          const data = await response.json();
          setAssistitoName(data.nome_assistito || 'Assistito');
        }
      } catch (error) {
        console.error('Errore caricamento info:', error);
      }
    };
    
    if (famigliaId) {
      fetchFamilyInfo();
    }
  }, [famigliaId]);
  
  // Invia l'alert di emergenza
  const sendEmergencyAlert = useCallback(async () => {
    setStatus('loading');
    
    try {
      const response = await fetch(`${API_URL}/api/scudo/alert`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          famiglia_id: famigliaId
        }),
      });
      
      if (response.ok) {
        setStatus('success');
        // Vibrazione di conferma se disponibile
        if (navigator.vibrate) {
          navigator.vibrate([200, 100, 200, 100, 200]);
        }
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.detail || 'Errore nell\'invio dell\'alert');
        setStatus('error');
      }
    } catch (error) {
      console.error('Errore invio alert:', error);
      setErrorMessage('Errore di connessione. Riprova.');
      setStatus('error');
    }
  }, [famigliaId]);
  
  // Gestisce il click sul pulsante
  const handleButtonClick = () => {
    if (status === 'loading' || status === 'success') return;
    
    // Reset error state
    if (status === 'error') {
      setStatus('idle');
      setClickCount(0);
    }
    
    const now = Date.now();
    setLastClickTime(now);
    
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    // Vibrazione feedback
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    
    // Al terzo click, invia l'alert
    if (newCount >= 3) {
      sendEmergencyAlert();
    }
  };
  
  // Reset per riprovare
  const handleReset = () => {
    setStatus('idle');
    setClickCount(0);
    setErrorMessage('');
  };
  
  // Rendering basato sullo stato
  const renderContent = () => {
    if (status === 'loading') {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-orange-500 to-red-600 p-6">
          <div className="text-center text-white">
            <Loader2 className="w-24 h-24 animate-spin mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-2">Invio in corso...</h2>
            <p className="text-xl opacity-90">Stiamo avvisando la tua famiglia</p>
          </div>
        </div>
      );
    }
    
    if (status === 'success') {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-green-500 to-green-600 p-6">
          <div className="text-center text-white">
            <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-20 h-20 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-2">Messaggio inviato!</h2>
            <p className="text-xl opacity-90 mb-8">La tua famiglia è stata avvisata</p>
            <p className="text-lg opacity-75">Arriveranno presto da te</p>
            
            <button
              onClick={handleReset}
              className="mt-12 px-8 py-4 bg-white/20 hover:bg-white/30 rounded-2xl text-white text-lg font-semibold transition-all"
            >
              Torna al pulsante
            </button>
          </div>
        </div>
      );
    }
    
    if (status === 'error') {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-gray-600 to-gray-800 p-6">
          <div className="text-center text-white">
            <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-20 h-20 text-yellow-300" />
            </div>
            <h2 className="text-3xl font-bold mb-2">Ops! Qualcosa non va</h2>
            <p className="text-xl opacity-90 mb-4">{errorMessage}</p>
            
            <button
              onClick={handleReset}
              className="mt-8 px-8 py-4 bg-red-500 hover:bg-red-600 rounded-2xl text-white text-lg font-semibold transition-all"
            >
              Riprova
            </button>
          </div>
        </div>
      );
    }
    
    // Stato idle - pulsante principale
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 p-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Shield className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Scudo CuraCheck</h1>
          {assistitoName && (
            <p className="text-gray-500">Ciao {assistitoName}</p>
          )}
        </div>
        
        {/* Pulsante Emergenza */}
        <div className="relative">
          {/* Cerchi pulsanti di sfondo */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className={`w-72 h-72 rounded-full bg-red-200 animate-ping opacity-20 ${clickCount > 0 ? 'block' : 'hidden'}`} />
          </div>
          
          <button
            onClick={handleButtonClick}
            className={`relative w-64 h-64 rounded-full shadow-2xl transition-all duration-200 transform active:scale-95 flex flex-col items-center justify-center ${
              clickCount === 0 
                ? 'bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' 
                : clickCount === 1
                  ? 'bg-gradient-to-br from-orange-500 to-red-500'
                  : 'bg-gradient-to-br from-yellow-500 to-orange-500'
            }`}
            style={{
              boxShadow: clickCount > 0 
                ? '0 0 60px rgba(239, 68, 68, 0.5)' 
                : '0 25px 50px -12px rgba(239, 68, 68, 0.4)'
            }}
          >
            <Heart className="w-20 h-20 text-white mb-2" fill="white" />
            <span className="text-white text-2xl font-bold">
              {clickCount === 0 ? 'EMERGENZA' : `${3 - clickCount} click`}
            </span>
          </button>
        </div>
        
        {/* Istruzioni */}
        <div className="mt-12 text-center max-w-sm">
          <div className="flex items-center justify-center gap-2 mb-4">
            {[1, 2, 3].map((num) => (
              <div 
                key={num}
                className={`w-4 h-4 rounded-full transition-all duration-300 ${
                  clickCount >= num 
                    ? 'bg-red-500 scale-125' 
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          
          <p className="text-gray-600 text-lg">
            {clickCount === 0 && 'Clicca 3 volte per chiamare aiuto'}
            {clickCount === 1 && 'Ancora 2 click...'}
            {clickCount === 2 && 'Ancora 1 click!'}
          </p>
          
          {clickCount > 0 && (
            <p className="text-gray-400 text-sm mt-2">
              (si azzera automaticamente tra pochi secondi)
            </p>
          )}
        </div>
        
        {/* Footer */}
        <div className="absolute bottom-6 text-center">
          <p className="text-gray-400 text-sm">
            Protetto da CuraCheck
          </p>
        </div>
      </div>
    );
  };
  
  return renderContent();
};

export default ScudoPage;
